import { Injectable } from '@angular/core';
import { endpoint } from '@tekman/onmat/constants';
import {
  Activity,
  ActivityStudentAnswers,
  ActivityStudentAverage,
  AverageGrade,
  EvaluationPage,
  ExerciseSolutionary,
  ExerciseTeacherGuide,
  ItineraryOption,
  Materials,
  Rubric,
  Status,
  StudentEvaluationStatus,
  StudentSubcompetences,
  SubCompetencesAssessmentStudents,
  Tags,
  TeachingInfo,
  UpdatedActivityFeature,
  StepsCategoryModel,
} from '@tekman/onmat/models';
import { ApiService } from '@tekman/onmat/service/generic';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ActivitiesTeacherService {
  isLoadingCursorSubject = new Subject<boolean>();
  isLoadingCursor$ = this.isLoadingCursorSubject.asObservable();

  isLoadingAnswersStudentSubject = new Subject<boolean>();
  isLoadingAnswersStudent$ = this.isLoadingAnswersStudentSubject.asObservable();

  isItineraryClickedSubject = new Subject<ItineraryOption>();
  isItineraryClickedSubject$ = this.isItineraryClickedSubject.asObservable();

  didacticSecuenceSubject = new Subject<string>();
  didacticSecuence$ = this.didacticSecuenceSubject.asObservable();

  isExportEvaluationClickedSubject = new Subject<any>();
  isExportEvaluationClicked$ = this.isExportEvaluationClickedSubject.asObservable();

  constructor(private readonly apiService: ApiService) {}

  getActivity(id: string, groupId: string): Observable<Activity> {
    const url = this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.activity.retrieve, '{id}', id), '{groupId}', groupId);
    return this.apiService.get<Activity>(url);
  }

  getRubrics(id: string): Observable<Rubric[]> {
    return this.apiService.get<Rubric[]>(this.apiService.replaceUrl(endpoint.activity.rubrics, '{id}', id));
  }

  getSteps(id: string, groupId: string): Observable<StepsCategoryModel> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.step.stepsStudent, '{id}', id),
      '{groupId}',
      groupId
    );
    return this.apiService.get<StepsCategoryModel>(url);
  }

  getTags(activityId: string, groupId: string): Observable<Tags[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.features.retrieve, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<Tags[]>(url);
  }

  getTeachingInfo(id: string): Observable<TeachingInfo> {
    return this.apiService.get<TeachingInfo>(this.apiService.replaceUrl(endpoint.activity.step.teachingInfo, '{id}', id));
  }

  getAverageGrades(activityId: string, groupId: string): Observable<AverageGrade[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.teacher.averageGrades, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<AverageGrade[]>(url);
  }

  getStudentsActivityStatus(activityId: string, groupId: string): Observable<StudentEvaluationStatus[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.student.status, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<StudentEvaluationStatus[]>(url);
  }

  getStudentsGroupSubcompetence(activityId: string, groupId: string): Observable<StudentSubcompetences[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.student.allStudentsSubcompetences, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<StudentSubcompetences[]>(url);
  }

  applySubcompetenceAssessmentStudents(
    activityId: string,
    groupId: string,
    subcompetencesAssessmentStudents: SubCompetencesAssessmentStudents
  ): Observable<any> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.student.allStudentsSubcompetences, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.post<any>(url, subcompetencesAssessmentStudents);
  }

  repeatActivity(activityId: string, groupId: string, studentId: string): Observable<Status> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.activity.evaluate.repeatActivity, '{id}', activityId),
        '{groupId}',
        groupId
      ),
      '{studentId}',
      studentId
    );
    return this.apiService.post<any>(url, null);
  }

  saveSubcompetencesStudent(activityId: string, groupId: string, subcompetencesAssessmentStudent: StudentSubcompetences): Observable<void> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.activity.student.grades, '{id}', activityId), '{groupId}', groupId),
      '{studentId}',
      subcompetencesAssessmentStudent.studentId
    );
    return this.apiService.post<any>(url, subcompetencesAssessmentStudent.subCompetenceGrade);
  }

  getTeacherGuide(activityId: string, exerciseId: string): Observable<ExerciseTeacherGuide> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.teacher.guide, '{id}', activityId),
      '{exerciseId}',
      exerciseId
    );
    return this.apiService.get<ExerciseTeacherGuide>(url);
  }

  getTeacherSolutionary(activityId: string, exerciseId: string): Observable<ExerciseSolutionary> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.teacher.solutionary, '{id}', activityId),
      '{exerciseId}',
      exerciseId
    );
    return this.apiService.get<ExerciseSolutionary>(url);
  }

  saveEvaluationPages(activityId: string, groupId: string, evaluationPage: EvaluationPage): Observable<EvaluationPage> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.activity.features.exercise, '{id}', activityId), '{groupId}', groupId),
      '{exerciseId}',
      evaluationPage.exerciseId
    );
    return this.apiService.post<EvaluationPage>(url, evaluationPage);
  }

  saveFeature(feature: UpdatedActivityFeature, activityId: string, groupId: string, studentId: string): Observable<UpdatedActivityFeature> {
    let url;
    if (studentId) {
      url = this.apiService.replaceUrl(
        this.apiService.replaceUrl(
          this.apiService.replaceUrl(endpoint.activity.features.studentSave, '{id}', activityId),
          '{groupId}',
          groupId
        ),
        '{studentId}',
        studentId
      );
    } else {
      url = this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.activity.features.save, '{id}', activityId),
        '{groupId}',
        groupId
      );
    }
    return this.apiService.post<UpdatedActivityFeature>(url, feature);
  }

  getMaterials(activityId: string, groupId: string): Observable<Materials> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(endpoint.activity.materials.retrieve, '{id}', activityId),
      '{groupId}',
      groupId
    );
    return this.apiService.get<Materials>(url);
  }

  getStudentAnswers(activityId: string, groupId: string, studentId: string): Observable<ActivityStudentAnswers[]> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(this.apiService.replaceUrl(endpoint.activity.student.answers, '{id}', activityId), '{groupId}', groupId),
      '{studentId}',
      studentId
    );
    return this.apiService.get<ActivityStudentAnswers[]>(url);
  }

  getStudentAnswersAverage(activityId: string, groupId: string, studentId: string): Observable<ActivityStudentAverage> {
    const url = this.apiService.replaceUrl(
      this.apiService.replaceUrl(
        this.apiService.replaceUrl(endpoint.activity.student.averageGrades, '{id}', activityId),
        '{groupId}',
        groupId
      ),
      '{studentId}',
      studentId
    );
    return this.apiService.get<ActivityStudentAverage>(url);
  }
}
